// 配額快照 hook（GET /v3/me/quota，restful 02 §7.2；spec 19 §6.1 tile badge 資料源）。
//
// owner-only：/v3/me/quota 本身即以 JWT.sub=owner_user_id 過濾 user-index（role=owner），
// shared device 不出現在回應 → 天然無 badge（§5.2 mask，client 無需再濾）。
//
// v1 單頁：apiFetch 不透出列表信封的 meta.next_cursor（02 §7.2），故以 limit=50 取單頁
// 涵蓋一般帳號（與 useDeviceList 同決策）；逼近上限時 console.warn 提示分頁未消費（G-7）。
//
// 優雅降級：端點不可用 / 網路異常 → 回空 Map（不顯任何 badge、不阻斷 Devices 頁，§6.1）。

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import type { QuotaEntry } from './quotaModel';

/** 單頁上限（02 §7.2 cursor 分頁；apiFetch 未透出 next_cursor，v1 不續頁）。 */
const QUOTA_PAGE_LIMIT = 50;

/** 空快照共用同一參考，避免錯誤/初始態在重繪間產生新物件造成下游 effect churn。 */
const EMPTY_QUOTA: ReadonlyMap<string, QuotaEntry> = new Map();

export interface DeviceQuotaState {
  /** device_id → 該 device 的配額信封（owner-only）；缺鍵 = 無配額資料 = 無 badge。 */
  quotaByDevice: ReadonlyMap<string, QuotaEntry>;
  reload: () => Promise<void>;
}

export function useDeviceQuota(): DeviceQuotaState {
  const [quotaByDevice, setQuotaByDevice] = useState<ReadonlyMap<string, QuotaEntry>>(EMPTY_QUOTA);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    try {
      const res = await apiFetch<QuotaEntry[]>('/v3/me/quota', { query: { limit: QUOTA_PAGE_LIMIT } });
      if (!alive.current) return;
      const list = Array.isArray(res.data) ? res.data : [];
      if (list.length >= QUOTA_PAGE_LIMIT) {
        // 命中單頁上限：可能有後續頁但 apiFetch 不透出 meta.next_cursor（G-7）→ 告警不阻斷。
        console.warn(`[quota] /v3/me/quota 回傳 ≥${QUOTA_PAGE_LIMIT} 筆，v1 未續頁（meta.next_cursor 未消費）。`);
      }
      const map = new Map<string, QuotaEntry>();
      for (const e of list) if (e.device_id) map.set(e.device_id, e);
      setQuotaByDevice(map);
    } catch {
      // 端點不可用 / 網路異常 → 不顯 badge（§6.1 優雅降級）。保持空快照同參考。
      if (alive.current) setQuotaByDevice(EMPTY_QUOTA);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { quotaByDevice, reload };
}
